import { LayoutCluster } from "../Topology/LayoutCluster";
import { TopoNode } from "../Topology/TopoNode";
import { Point, Matrix } from "../Utils/Matrix";
import { StringUtils } from "../Utils/StringUtils";
import { TopoMap } from "../Topology/TopoMap";
import { List } from "../Utils/List";
import { SpanningTree } from "../Topology/SpanningTree";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";


export enum LayoutDirection { Left, Right, Up, Down }

export enum RootAlignment { Left, Center, Right }

export enum Orientation { Vertical, Horizontal }


export class HierarchicalLayoutCluster extends LayoutCluster<HierarchicalLayoutCluster> {

    constructor(root: TopoNode) {
        super(root);
    }

	public Place(location: Point): void {
        var xOffset: number = location.X - this.Left;
        var yOffset: number = location.Y - this.Top;

        this.Root.Offset(xOffset, yOffset);

        this.Clusters.ForEach((cluster) => {
            cluster.Offset(xOffset, yOffset);
        }, this);
    }

    public AlignRoot(alignment: RootAlignment, orientation: Orientation): void {
        if (orientation == Orientation.Horizontal) {
            switch (alignment) {
                case RootAlignment.Center:
                    this.Root.X = this.Left + this.Width / 2;
                    break;
                case RootAlignment.Left:
                    this.Root.X = this.Left + this.RootSize.Width / 2;
                    break;
                case RootAlignment.Right:
                    this.Root.X = this.Right - this.RootSize.Width / 2;
                    break;
            }
        }
        else {
            switch (alignment) {
                case RootAlignment.Center:
                    this.Root.Y = this.Top + this.Height / 2;
                    break;
                case RootAlignment.Left:
                    this.Root.Y = this.Top + this.RootSize.Height / 2;
                    break;
                case RootAlignment.Right:
                    this.Root.Y = this.Bottom - this.RootSize.Height / 2;
                    break;
            }
        }
    }
}


//export interface HierarchicalLayoutSettingsJson {
//    level_spacing: string;
//    node_spacing: string;
//    layout_direction: string;
//    root_alignment: string;
//    use_straight_links: string;
//}


export class HierarchicalLayoutSettings implements ObjectSerializer {

    private static DefaultSpacing = 100;

	constructor() {
        this.LevelSpacing = HierarchicalLayoutSettings.DefaultSpacing;
        this.NodeSpacing = HierarchicalLayoutSettings.DefaultSpacing;
        this.LayoutDirection = LayoutDirection.Down;
        this.RootAlignment = RootAlignment.Center;
        this.UseStraightLinks = false;
    }

    public LevelSpacing: number;
    public NodeSpacing: number;
    public LayoutDirection: LayoutDirection;
    public RootAlignment: RootAlignment;
    public UseStraightLinks: boolean;

    public Clone(): HierarchicalLayoutSettings {
        var result = new HierarchicalLayoutSettings();

        result.LevelSpacing = this.LevelSpacing;
        result.NodeSpacing = this.NodeSpacing;
        result.LayoutDirection = this.LayoutDirection;
        result.RootAlignment = this.RootAlignment;
        result.UseStraightLinks = this.UseStraightLinks;

        return result;
    }

    //public static FromJson(json: HierarchicalLayoutSettingsJson) {
    //    var result = new HierarchicalLayoutSettings();

    //    if (json.level_spacing !== undefined)
    //        result.LevelSpacing = parseInt(json.level_spacing);

    //    if (json.node_spacing !== undefined)
    //        result.NodeSpacing = parseInt(json.node_spacing);

    //    if (json.layout_direction !== undefined)
    //        result.LayoutDirection = LayoutDirection[json.layout_direction];

    //    if (json.root_alignment !== undefined)
    //        result.RootAlignment = RootAlignment[json.root_alignment];

    //    if (json.use_straight_links !== undefined)
    //        result.UseStraightLinks = StringUtils.ParseBool(json.use_straight_links);

    //    return result;
    //}

	public Write(obj: any, context: any): void {
		obj.hierarchical_layout_settings = {};
		obj = obj.hierarchical_layout_settings;

		obj.level_spacing = ValueEncoder.EncodeInteger(this.LevelSpacing);
		obj.node_spacing = ValueEncoder.EncodeInteger(this.NodeSpacing);
		obj.straight_links = ValueEncoder.EncodeBoolean(this.UseStraightLinks);
		obj.layout_direction = LayoutDirection[this.LayoutDirection];
		obj.root_alignment = RootAlignment[this.RootAlignment];
	}

	public Read(obj: any, context: any): void {
		if (!obj.hierarchical_layout_settings)
			throw new Error();
		obj = obj.hierarchical_layout_settings;

		if (obj.level_spacing !== undefined)
			this.LevelSpacing = ValueEncoder.DecodeInteger(obj.level_spacing);
		if (obj.node_spacing !== undefined)
			this.NodeSpacing = ValueEncoder.DecodeInteger(obj.node_spacing);
		if (obj.straight_links !== undefined)
			this.UseStraightLinks = ValueEncoder.DecodeBoolean(obj.straight_links);
		if (obj.layout_direction !== undefined)
			this.LayoutDirection = LayoutDirection[<string>obj.layout_direction];
		if (obj.root_alignment !== undefined)
			this.RootAlignment = RootAlignment[<string>obj.root_alignment];
	}

}


export class HierarchicalLayout {
    private static DefaultTreeMargin = 100;

    public LevelSpacing: number;
    public NodeSpacing: number;

    private _layoutDirection: LayoutDirection;
    public get LayoutDirection(): LayoutDirection {
        return this._layoutDirection;
    }
    public set LayoutDirection(value: LayoutDirection) {
        this._layoutDirection = value;
        if (this._layoutDirection == LayoutDirection.Left ||
            this._layoutDirection == LayoutDirection.Right)
            this._orientation = Orientation.Vertical;
        else
            this._orientation = Orientation.Horizontal;
    }

    public RootAlignment: RootAlignment;
    public TreeMargin: number;
    private _orientation: Orientation = Orientation.Horizontal;

    private _topoMap: TopoMap;

	constructor (topoMap: TopoMap) {
        this._topoMap = topoMap;
        this.Settings = topoMap.LayoutSettings.HierarchicalSettings;
        this.TreeMargin = HierarchicalLayout.DefaultTreeMargin;
    }

	public set Settings(value: HierarchicalLayoutSettings) {
        this.LevelSpacing = value.LevelSpacing;
        this.NodeSpacing = value.NodeSpacing;
        this.LayoutDirection = value.LayoutDirection;
        this.RootAlignment = value.RootAlignment;
    }

    public LayoutSpanningTrees(spanningTrees: List<SpanningTree>): void {

        spanningTrees.ForEach((tree) => {
            tree.Nodes.ForEach((node) => {
                node.Location = new Point(0, 0);
            }, this);

            this.LayoutNodes(tree.Root, tree);
        }, this);

        // Offset the trees
        if (spanningTrees.Count > 0) {
            var firstTree: SpanningTree = spanningTrees.Get(0);
            firstTree.CalculateSize();
            var leftX: number = firstTree.Right;

            for (var i = 1; i < spanningTrees.Count; i++) {
                var tree: SpanningTree = spanningTrees.Get(i);
                tree.CalculateSize();
                var xOffset: number = leftX - tree.Left + this.TreeMargin;
                leftX += tree.Width + this.TreeMargin;

                tree.Offset(xOffset, 0);
            }
        }
    }

    public LayoutChildren(node: TopoNode): HierarchicalLayoutCluster {

        var cluster: HierarchicalLayoutCluster = null;
        var tree: SpanningTree = this._topoMap.OwningTree(node);
        if (tree != null) {
            var nodeLocation: Point = node.Location;

            node.Location = new Point(0, 0);

            this._topoMap.DescendantNodes(node).ForEach((d) => {
                d.Location = new Point(0, 0);
            }, this);

            var cluster = this.LayoutNodes(node, tree);

            cluster.Offset(nodeLocation.X - node.X, nodeLocation.Y - node.Y);
        }
        return cluster;
    }

    public LayoutNodes(root: TopoNode, tree: SpanningTree): HierarchicalLayoutCluster {
        var currentCluster: HierarchicalLayoutCluster = new HierarchicalLayoutCluster(root);
        currentCluster.CalculateSize();

        tree.ChildNodes(root).ForEach((child) => {
            currentCluster.AddCluster(this.LayoutNodes(child, tree));
        }, this);

        var clusters: List<HierarchicalLayoutCluster> = currentCluster.Clusters;
        if (clusters.Count > 0) {
            var nextLocation: number;
            if (this._orientation == Orientation.Horizontal)
                nextLocation = currentCluster.Left;
            else
                nextLocation = currentCluster.Top;

            clusters.ForEach((cluster) => {
                var loc: Point;
                if (this.LayoutDirection == LayoutDirection.Down)
                    loc = new Point(nextLocation, this.LevelSpacing);
                else if (this.LayoutDirection == LayoutDirection.Up)
                    loc = new Point(nextLocation, -this.LevelSpacing - cluster.Height);
                else if (this.LayoutDirection == LayoutDirection.Right)
                    loc = new Point(this.LevelSpacing, nextLocation);
                else
                    loc = new Point(-this.LevelSpacing - cluster.Width, nextLocation);

                cluster.Place(loc);

                if (this._orientation == Orientation.Horizontal)
                    nextLocation = nextLocation + this.NodeSpacing + cluster.Width;
                else
                    nextLocation = nextLocation + this.NodeSpacing + cluster.Height;
            }, this);
        }

        currentCluster.CalculateSize();
        currentCluster.AlignRoot(this.RootAlignment, this._orientation);
        return currentCluster;
    }
}
 